import { MAGIC_MISSILE_CHILD, MAGIC_EFFECT_CAPABILITY, magicCatalog, magicClockSample, resolveMagicEffect, selectMagicProjectiles,
  type MagicEffect, type MagicEffectCatalog, type MagicSelection } from '../shared/magic-effect-contract';
import { prepareMagicAudio, scheduleMagicAudio, cancelMagicAudio, magicAudioReceipt, magicAudioState } from './magic-effect-audio';
import { currentFt3dSceneDocument, currentFt3dSceneWindow } from '../ft3d-hud/scene-frame';

type Host = { ws?: WebSocket; ftGetActiveEncounterId?: () => string; ftFindFullNestedItem?: (id: string) => unknown;
  FateTide?: { user?: { id?: string }; objectCache?: { get: (id: string) => unknown } }; };
declare global { interface Window {
  ftPrepareMagicSelection?: (count: number) => MagicSelection | null;
  ftMagicEffectConnectionId?: string;
  ftMagicEffectDiagnostics?: { events: Array<Record<string, unknown>>; audio: Array<Record<string, unknown>>; clock: unknown; readonly audioState: string };
} }
const host = window as unknown as Host;
const enabled = new URLSearchParams(location.search).get('magicMissilePilot') === '1';
const events: Array<Record<string, unknown>> = [];
const seen = new Set<string>();
const pending = new Map<string, { event: MagicEffect; catalog: MagicEffectCatalog; eligible: boolean; impactAt: number }>();
let currentEncounter = '', announced = '', currentSocket: WebSocket | undefined;
let subscribedEffect = '';
let preparation: { revision: string; document: Document; requestId: string; sceneReady: boolean; audioReady: boolean; announced: boolean; lastSent: number } | null = null;
let clock: { offsetMs: number; rttMs: number; at: number } | null = null;
let mounted = false;
const report = (row: Record<string, unknown>) => { events.push(row); if (events.length > 300) events.shift(); };
function cachedCatalog(): MagicEffectCatalog | null {
  for (const candidate of [host.ftFindFullNestedItem?.(MAGIC_MISSILE_CHILD), host.FateTide?.objectCache?.get(MAGIC_MISSILE_CHILD)]) {
    const def = candidate as { assets?: { magic_effect?: unknown } } | null;
    const resolved = magicCatalog(def?.assets?.magic_effect); if (resolved) return resolved;
  }
  return null;
}
function eligible(encounter: string): boolean { return enabled && !document.hidden && currentEncounter === encounter && !!currentFt3dSceneWindow(); }
function clear(): void {
  for (const id of pending.keys()) currentFt3dSceneWindow()?.postMessage({ type: 'ft3d-magic-effect-cancel', eventId: id }, location.origin);
  pending.clear(); cancelMagicAudio();
}
export function mountMagicEffects(): void {
  // The embedded scene delegates scheduling to its exact parent connection. It retains its
  // ordinary gameplay subscriptions but must not be mistaken for a legacy top-level viewer.
  if (window.parent !== window && !mounted) {
    mounted = true;
    let binding = '';
    window.setInterval(() => {
      try {
        const owner = window.parent as Window;
        const id = owner.ftMagicEffectConnectionId;
        if (!id || !owner.ftPrepareMagicSelection || host.ws?.readyState !== WebSocket.OPEN) return;
        if (currentSocket !== host.ws) { currentSocket = host.ws; binding = ''; }
        if (binding !== id) {
          host.ws.send(JSON.stringify({ type: 'transport_capability', capability: 'ft.magic-effect.delegate.v1', ownerConnectionId: id }));
          binding = id;
        }
      } catch { /* A cross-origin frame cannot delegate to this coordinator. */ }
    }, 250);
    return;
  }
  if (!enabled || mounted) return;
  mounted = true;
  window.ftMagicEffectDiagnostics = { events, audio: magicAudioReceipt, clock, get audioState() { return magicAudioState(); } };
  window.ftPrepareMagicSelection = count => {
    const available = cachedCatalog();
    if (!available || !clock || performance.now() - clock.at > 20000 || host.ws?.readyState !== WebSocket.OPEN
      || announced !== available.catalogRevision || subscribedEffect !== `effect:${currentEncounter}:${host.FateTide?.user?.id?.toUpperCase()}`
      || !preparation?.announced || preparation.document !== currentFt3dSceneDocument()
      || !eligible(currentEncounter)) throw new Error('Magic Missile effects are still preparing. Refresh the encounter before casting.');
    return selectMagicProjectiles(available, count);
  };
  window.addEventListener('message', message => {
    if (message.origin !== location.origin || message.source !== currentFt3dSceneWindow()) return;
    if (message.data?.type === 'ft3d-magic-effect-prepared') {
      if (!preparation || preparation.document !== currentFt3dSceneDocument() || message.data.requestId !== preparation.requestId) return;
      preparation.sceneReady = message.data.accepted === true;
      report({ status: 'scene-prepared', accepted: preparation.sceneReady, requestId: preparation.requestId, revision: preparation.revision, at: performance.now() });
      return;
    }
    if (message.data?.type !== 'ft3d-magic-effect-state') return;
    const id = String(message.data.eventId || ''), state = pending.get(id); if (!state) return;
    if (!message.data.accepted) { pending.delete(id); cancelMagicAudio(id); report({ eventId: id, status: 'scene-rejected', at: performance.now(), reason: message.data.reason }); return; }
    if (state.eligible) return;
    state.eligible = true;
    const resolved = resolveMagicEffect(state.event, state.catalog); if (!resolved) return;
    report({ eventId: id, status: 'scene-ready', at: performance.now(), scene: message.data });
    void scheduleMagicAudio(id, resolved.audio, state.impactAt, () => pending.get(id)?.eligible === true && eligible(state.event.encounterId));
  });
  document.addEventListener('visibilitychange', () => { if (document.hidden) clear(); });
  window.addEventListener('pagehide', clear);
  let lastClock = 0;
  window.setInterval(() => {
    const encounter = String(host.ftGetActiveEncounterId?.() || '').toUpperCase();
    const socket = host.ws;
    if (encounter !== currentEncounter || socket !== currentSocket) {
      if (currentSocket?.readyState === WebSocket.OPEN && currentEncounter && host.FateTide?.user?.id)
        currentSocket.send(JSON.stringify({ type: 'unsubscribe', channel: `effect:${currentEncounter}:${host.FateTide.user.id}` }));
      clear(); currentEncounter = encounter; currentSocket = socket; announced = ''; subscribedEffect = ''; clock = null; preparation = null;
    }
    if (!socket || socket.readyState !== WebSocket.OPEN || !currentEncounter || !host.FateTide?.user?.id) return;
    const next = cachedCatalog();
    if (next && next.catalogRevision !== announced) {
      announced = next.catalogRevision;
      socket.send(JSON.stringify({ type: 'subscribe', channel: `effect:${currentEncounter}:${host.FateTide.user.id}` }));
    }
    const sceneDocument = currentFt3dSceneDocument();
    if (preparation && (preparation.document !== sceneDocument || preparation.revision !== next?.catalogRevision)) {
      preparation = null;
      socket.send(JSON.stringify({ type: 'transport_capability', capability: null }));
      clear();
    }
    if (next && sceneDocument?.readyState === 'complete') {
      if (!preparation) {
        const state = { revision: next.catalogRevision, document: sceneDocument, requestId: crypto.randomUUID(), sceneReady: false, audioReady: false, announced: false, lastSent: -Infinity };
        preparation = state;
        void Promise.all(next.impact.map(prepareMagicAudio)).then(() => { if (preparation === state) state.audioReady = true; })
          .catch(error => { if (preparation === state) preparation = null; report({ status: 'preload-failed', error: String(error) }); });
      }
      if (!preparation.sceneReady && performance.now() - preparation.lastSent >= 1000) {
        preparation.lastSent = performance.now();
        currentFt3dSceneWindow()?.postMessage({ type: 'ft3d-magic-effect-preload', requestId: preparation.requestId, recipes: next.variants.map(v => v.recipe) }, location.origin);
      }
      if (preparation.sceneReady && preparation.audioReady && !preparation.announced) {
        socket.send(JSON.stringify({ type: 'transport_capability', capability: MAGIC_EFFECT_CAPABILITY, catalogRevision: next.catalogRevision }));
        preparation.announced = true;
        report({ status: 'media-prepared', revision: next.catalogRevision, at: performance.now() });
      }
    }
    if (performance.now() - lastClock > (clock ? 5000 : 500)) {
      lastClock = performance.now(); socket.send(JSON.stringify({ type: 'transport_clock', sentAt: performance.timeOrigin + performance.now() }));
    }
    for (const [id, state] of pending) if (clock && performance.timeOrigin + performance.now() + clock.offsetMs > state.event.expiresAtUnixMs + 1000) pending.delete(id);
  }, 250);
}
/** Connection controls are consumed before ordinary gameplay routing. */
export function consumeMagicTransportControl(message: { type?: string; sentAt?: number; serverTimeUnixMs?: number; connectionId?: string; channel?: string; status?: string }): boolean {
  if (message.type === 'transport_subscription' && message.channel === `effect:${currentEncounter}:${host.FateTide?.user?.id?.toUpperCase()}`) {
    subscribedEffect = message.status === 'subscribed' ? message.channel : '';
    return true;
  }
  if (message.type === 'transport_ready') {
    window.ftMagicEffectConnectionId = message.connectionId;
    return false;
  }
  if (message.type === 'transport_capability') return true;
  if (message.type !== 'transport_clock') return false;
  if (!enabled || typeof message.sentAt !== 'number' || typeof message.serverTimeUnixMs !== 'number') return true;
  const sample = magicClockSample(message.sentAt, performance.timeOrigin + performance.now(), message.serverTimeUnixMs);
  if (sample && (!clock || sample.rttMs <= clock.rttMs || performance.now() - clock.at > 15000)) clock = { ...sample, at: performance.now() };
  if (window.ftMagicEffectDiagnostics) window.ftMagicEffectDiagnostics.clock = clock;
  return true;
}
export function handleMagicEffect(event: MagicEffect): void {
  if (!enabled || seen.has(event.eventId)) return;
  const available = cachedCatalog();
  const resolved = available && resolveMagicEffect(event, available);
  if (!resolved || !available || !clock || performance.now() - clock.at > 20000 || !eligible(event.encounterId)) {
    report({ eventId: event.eventId, status: 'skipped', reason: 'catalog-clock-or-context' }); return;
  }
  const startAt = event.startAtUnixMs - clock.offsetMs - performance.timeOrigin;
  if (performance.now() >= startAt + event.durationMs || pending.size >= 48) { report({ eventId: event.eventId, status: 'skipped', reason: 'expired-or-budget' }); return; }
  seen.add(event.eventId); if (seen.size > 2000) seen.delete(seen.values().next().value!);
  pending.set(event.eventId, { event, catalog: available, eligible: false, impactAt: startAt + event.impactOffsetMs });
  report({ ...event, status: 'received', startAt, receivedAt: performance.now(), clock: { ...clock } });
  // This lane carries its own server-scheduled clock; the shared cast clock (castTiming) is the roll lane's.
  const { castTiming: _castTiming, vfxDelayMs: _vfxDelayMs, ...recipe } = resolved.variant.recipe;
  currentFt3dSceneWindow()?.postMessage({ type: 'ft3d-magic-effect-play', payload: {
    schema: 'ft.ft3d.magic-cue.v1', id: event.eventId, sourceEntityId: event.sourceEntityId, targetEntityId: event.targetEntityId,
    recipe, scheduledStartEpochMs: performance.timeOrigin + startAt,
    impactOffsetMs: event.impactOffsetMs, durationMs: event.durationMs,
  } }, location.origin);
}
